// @ts-strict-ignore
import { Subject, Subscription } from "rxjs";
import { RBEvent } from 'rainbow-web-sdk/models/event.model';
import anonymizer from 'rainbow-web-sdk/utils/anonymizer';
import { Contact } from 'rainbow-web-sdk/models/contact.model';
import { ErrorHelperInfo, ErrorHelperService } from 'rainbow-web-sdk/services/utilities/errorHelper.service';
import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { ContactService } from 'rainbow-web-sdk/services/core/contact.service';
import { AuthService } from 'rainbow-web-sdk/services/core/auth.service';
import { ProfileService } from 'rainbow-web-sdk/services/core/profile.service';
import { ConversationServiceRB } from "rainbow-web-sdk/services/conversation/conversation.service";
import { MainService } from 'rainbow-web-sdk/services/core/main.service';
import { Service } from "rainbow-web-sdk/services/service";
import { CompanyScopeType, DirectoryInternalSearchResults, DirectorySearchOptions } from "../core/directorySearch.service";

export const DIRECTORY_SVC = 'DirectoryService';

// Add SDK class making it an iterable contract for the future expansions
class SdkApis {
    constructor(
        // public search: any = null,
        public searchUserByLogin: any = null,
        public searchUserByJid: any = null,
        public searchUserByPhoneNumber: any = null,
        public searchAllContacts: any = null,
    ) { }
}


/**
 * Rainbow Directory service allows access to contacts search
 * SDK supports only the APIs listed in the _sdkApis array (you can access it through i.e. rainbowSDK.directoryService._sdkApis)
 */
export class DirectoryService extends Service implements SdkApis {
    // export class DirectoryService extends AbstractDirectoryService {

    private centralizedService: any;
    private authService: AuthService = AuthService.getInstance();
    private profileService: ProfileService = ProfileService.getInstance();
    private contactService: ContactService = ContactService.getInstance();
    private conversationService: ConversationServiceRB = ConversationServiceRB.getInstance();
    private logger: LoggerService = LoggerService.getInstance();
    private errorHelperService: ErrorHelperService = ErrorHelperService.getInstance();
    private mainService: MainService = MainService.getInstance();
    public _sdkApis: any[] = Object.keys(new SdkApis());

    // private companyContactsNumber: number = 0;
    // private otherCompanyContactsNumber: number = 0;

    private rxSubject: Subject<RBEvent>;

    public static getInstance() {
        return Service.create(DIRECTORY_SVC, DirectoryService);
    }

    public static build() {
        return new DirectoryService();
    }

    private constructor() {
        super();
        this.rxSubject = new Subject<RBEvent>();
    }

    // ====================================================
    //                  LIFE CYCLE STUFF
    // ====================================================

    public async start(): Promise<void> {
        try {
            this.logger.info("[DirectoryService] === STARTING ===");
            this.centralizedService = this.mainService.inject('CentralizedService');

            // Now service is started
            this.logger.info("[DirectoryService] === STARTED ===");
        }
        catch (error) {
            this.logger.error(`[DirectoryService] === STARTING FAILURE === ${error.message}`);
        }
    }

    public async stop(): Promise<void> {
        this.logger.info("[DirectoryService] === STOPPING ===");
        this.logger.info("[DirectoryService] === STOPPED ===");
    }


    public subscribe(observer: any): Subscription {
        return this.rxSubject.subscribe(observer);
    }

    // ====================================================
    //                  PUBLIC FUNCTIONS
    // Used by internal Services/Components/... or by SDK
    // ====================================================

    public async searchRainbowUsers(searchPattern: string, options?: DirectorySearchOptions, filterOwnUser: boolean = true): Promise<DirectoryInternalSearchResults> {
        this.logger.info(`[DirectoryService] search Rainbow users with pattern ${anonymizer(searchPattern)}`);

        try {
            const searchOptions: any = {};

            if (options?.limit) { searchOptions.limit = options.limit; }

            // Simple OR Multi criteria search ?
            const searchByTagAllowed: boolean = this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.SEARCH_BY_TAGS);
            let searchType: string = "";

            if (options?.extendedMode) {
                if (searchByTagAllowed) {
                    searchType = options.extendedMode ? "search" : "displayName";
                } else {
                    if (options.extendedMode) {
                        this.logger.warn(`[DirectoryService] searchRainbowUsers -- 'extendedMode' option not allowed and ignored `);
                    }
                    searchType = "displayName";
                }
            } else {
                searchType = "displayName";
            }
            if (searchType === "displayName") {
                searchOptions.displayName = searchPattern;
            } else {
                searchOptions.search = searchPattern;
            }

            if (options?.company?.companyScope) {
                // checking the consistency of information with the directory type
                switch (options?.company?.companyScope) {
                    case CompanyScopeType.MY_COMPANY:
                        searchOptions.companyId = this.contactService.userContact.company.id;
                        break;

                    case CompanyScopeType.MY_COMPANY_EXCLUDED:
                        searchOptions.excludeCompanyId = this.contactService.userContact.company.id;
                        break;

                    default:
                        break;
                }
            }

            // Build Request for Server
            const url: URL = new URL(`${config.restServerUrl}/api/rainbow/search/v1.0/users`);
            const urlParams: string = new URLSearchParams(searchOptions).toString();
            const headers: any = this.authService.getRequestHeader();
            url.search = urlParams;
            const response: any = await fetch(url.toString(), { method: 'GET', headers: headers });

            if (!response.ok) { throw (response); }
            const contactsData: any = (await response.json()).data;

            this.logger.info(`[DirectoryService] search success -- Found ${contactsData.total} contact(s)`);

            const contacts: Contact[] = [];
            contactsData.forEach((contactData: any) => {
                // Ignore userContact
                if (filterOwnUser && this.contactService.isUserContactJid(contactData.jid_im)) {
                    this.logger.info(`[DirectoryService] search -- contact ${contactData.jid_im} ignored`);
                    return;
                }

                // Check if this contact already exists in contactService
                let contact: Contact = this.contactService.getContactFromCacheByJid(contactData.jid_im);

                // Else create temporary contact
                if (!contact) {
                    contact = this.contactService.createBasicContact(contactData.jid_im);
                    contact.updateFromUserData(contactData);
                    this.contactService.dbContacts[contactData.id] = contact;
                    this.contactService.jtelContacts[contact.jidtel] = contact;
                } else {
                    contact.updateFromUserData(contactData);
                }

                contact.tags = contactData.tags;
                contact.dbId = contactData.id;
                this.contactService.updateContactRichStatus(contact);
                this.conversationService.computeCapabilitiesForContact(contact, false);
                contacts.push(contact);
            });

            contacts.sort(function(item1, item2) {
                // Sort by _displayName
                return item1.displayName < item2.displayName ? -1 : 1;
            });
            this.logger.info(`[DirectoryService] search -- Sorted contacts contains ${contacts.length} contact(s)`);

            // Warning: The server does NOT return the total number of results matching the search criteria.
            // So the 'total' field is forced to undefined
            return { contacts: contacts, total: undefined };
        } catch (error) {
            const errorInfo: ErrorHelperInfo = await this.errorHelperService.getErrorInfo(error, "searchRainbowUsers");
            this.logger.error(`[DirectoryService] search failure -- ${errorInfo.error.message}`);
            if (error.data && error.data.errorCode === 401) {
                this.rxSubject.next(RBEvent.create('ON_SESSION_EXPIRED_NOTIFICATION_EVENT'));
            }
            throw errorInfo.error;
        }
    }

    /**
     * Method searchUserByLogin
     * @description Search for a contact through its email address (used for login).
     * @memberof DirectoryService
     * @public
     * @async
     * @param {string} loginEmail User email address (used for login) used as search criteria
     * @returns {Promise} Promise that resolves with a contact matching the provided loginEmail or rejects with an Error
     */
    public async searchUserByLogin(loginEmail: string): Promise<Contact> {
        this.logger.info(`[DirectoryService] searchUserByLogin with "${anonymizer(loginEmail)}"`);

        try {
            // Prepare and send POST request url to Server
            const serverUrl = config.restServerUrl + "/api/rainbow/enduser/v1.0/users/loginEmails";
            const headers = this.authService.getPostHeader();
            const body = JSON.stringify({ loginEmail: [loginEmail] });

            const responseData: Response = await fetch(serverUrl, { method: 'POST', headers: headers, body: body });
            if (!responseData.ok) { throw responseData; }
            const userArray = (await responseData.json()).data;

            this.logger.info(`[DirectoryService] searchUserByLogin -- Search found ${userArray.length} contact(s)`);

            if (userArray.length === 1 /*&& userArray[0].isActive*/) { //CR_10870
                const contact = await this.contactService.getOrCreateContact(userArray[0].jid_im);
                // Add loginEmail to contact (missed after GPRD)
                contact.loginEmail = loginEmail;
                return contact;
            }
            return null;
        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "searchUserByLogin");
            this.logger.error(`[DirectoryService] searchUserByLogin failure -- ${errorInfo.error.message}`);
            if (error.data && error.data.errorCode === 401) {
                this.rxSubject.next(RBEvent.create('ON_SESSION_EXPIRED_NOTIFICATION_EVENT'));
            }
            throw errorInfo.error;
        }
    }

    /**
     * Method searchUserByJid
     * @description Search for a contact through its jid.
     * @memberof DirectoryService
     * @public
     * @async
     * @param {string} jid User jid_im used as search criteria
     * @returns {Promise} Promise that resolves with a contact matching the jid provided or rejects with an Error
     */
    public async searchUserByJid(jid: string): Promise<Contact> {
        this.logger.info(`[DirectoryService] searchUserByJid with "${anonymizer(jid)}"`);

        try {
            const serverUrl = config.restServerUrl + "/api/rainbow/enduser/v1.0/users/jids/" + encodeURIComponent(jid);
            const headers = this.authService.getRequestHeader();

            const responseData = await fetch(serverUrl, { method: "GET", headers });
            if (!responseData.ok) throw responseData;
            const user: any = (await responseData.json()).data;

            if (user) {
                this.logger.info(`[DirectoryService] searchUserByJid -- Search found 1 user`);
                const contact = await this.contactService.getOrCreateContact(jid);
                contact.updateFromUserData(user);
                return contact;
            }
            this.logger.info(`[DirectoryService] searchUserByJid -- Search found 0 user`);
            return null;
        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "searchUserByJid");
            this.logger.error(`[DirectoryService] searchUserByJid failure -- ${errorInfo.error.message}`);
            if (error.data && error.data.errorCode === 401) {
                this.rxSubject.next(RBEvent.create('ON_SESSION_EXPIRED_NOTIFICATION_EVENT'));
            }
            throw errorInfo.error;
        }
    }

    /**
     * Method searchUserByPhoneNumber
     * @description Search for a contact being associated to the requested phone number.
     * @memberof DirectoryService
     * @public
     * @async
     * @param {string} phoneNumber Phone number to search used as search criteria
     * @returns {Promise} Promise that resolves with a contact matching the phoneNumber provided or rejects with an Error
     */
    public async searchUserByPhoneNumber(phoneNumber: string): Promise<Contact> {
        this.logger.info(`[DirectoryService] searchUserByPhoneNumber with "${phoneNumber}"`);

        try {
            // Forge the request
            const serverUrl = config.restServerUrl + "/api/rainbow/search/v1.0/phone-numbers/" + phoneNumber + "/users";
            const headers = this.authService.getRequestHeader();

            const responseData = await fetch(serverUrl, { method: "GET", headers });
            if (!responseData.ok) throw responseData;
            const user: any = (await responseData.json()).data;
            if (user) {
                this.logger.info(`[DirectoryService] searchUserByPhoneNumber -- Search found 1 user`);
                const contact = await this.contactService.getOrCreateContact(user.jid_im);
                return contact;
            }
            this.logger.info(`[DirectoryService] searchUserByPhoneNumber -- Search found 0 user`);
            return null;
        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "searchUserByPhoneNumber");
            this.logger.error(`[DirectoryService] searchUserByPhoneNumber failure -- ${errorInfo.error.message}`);
            if (error.data && error.data.errorCode === 401) {
                this.rxSubject.next(RBEvent.create('ON_SESSION_EXPIRED_NOTIFICATION_EVENT'));
            }
            throw errorInfo.error;
        }
    }


    /**
     * Method searchAllContacts
     * @description Search for a contact matching a string.
     * @memberof DirectoryService
     * @public
     * @async
     * @param {string} searchText text to search
     * @param {number} [limit]  Max number of result. Default: 20
     * @param {string} [companyId] ID of the company on which users are searched. Default = ""
     * companyId and excludeCompanyId parameters are exclusives, companyId parameter can only be set if excludeCompanyId parameter is not provided.
     * @param {boolean} [addRosters] whether or not to include our contact roster in the search. Default: true
     * @param {boolean} [addOutlook] whether or not to include our outlook contacts in the search. Default: true
     * @param {string} [excludeCompanyId] Exclude users being in the requested company ID from the search results. Default = null
     * companyId and excludeCompanyId parameters are exclusives, excludeCompanyId parameter can only be set if companyId parameter is not provided.
     * @returns {Promise} Promise that resolves with an array containing a list of contacts matching the searchText or rejects with an Error
     */
    public async searchAllContacts(searchText: string, limit: number = 20, companyId: string = "", addRosters: boolean = true, addOutlook: boolean = true, excludeCompanyId: string = null, filterOwnUser: boolean = true): Promise<any[]> {
        this.logger.info(`[DirectoryService] searchAllContacts with text "${searchText}"`);
        try {
            let contacts = [];

            // Search local roster contacts
            if (addRosters) {
                contacts = this.contactService.searchRainbowUsersNetwork(searchText).contacts;
            }

            // Search Rainbow users via 'searchRainbowUsers
            const searchOptions: DirectorySearchOptions = {
                limit: limit,
                extendedMode: true,
            };

            if (companyId) {
                searchOptions.company = {
                    companyScope: CompanyScopeType.MY_COMPANY
                };
            } else if (excludeCompanyId) {
                searchOptions.company = {
                    companyScope: CompanyScopeType.MY_COMPANY_EXCLUDED
                };
            }
            const searchUsersResults: DirectoryInternalSearchResults = await this.searchRainbowUsers(searchText, searchOptions, filterOwnUser);
            const rainbowContacts = searchUsersResults.contacts;

            contacts = contacts.concat(rainbowContacts.filter((item: any) => { return !item.roster || (contacts.indexOf(item) < 0); }));
            this.logger.info(`[DirectoryService] searchAllContacts with text "${searchText}" - Found contacts = ${contacts.length}`);

            // Search Outlook contacts
            if (addOutlook && this.centralizedService && this.centralizedService.isDesktopApp() && this.centralizedService.outlook) {
                const [outlookContactsByEmail, outlookContactsByName] = await Promise.all([this.searchOutlookUsersByEmail(searchText), this.searchOutlookUsersByName(searchText)]);
                let outlookResults = this.centralizedService.outlook.mergeOutlookContact(outlookContactsByEmail, outlookContactsByName);
                outlookResults = this.adaptOutlookContacts(outlookResults);
                contacts = contacts.concat(outlookResults);
                this.logger.info(`[DirectoryService] searchAllContacts with text "${searchText}" including outlook contacts - Found contacts = ${contacts.length}`);
            }
            return contacts;
        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, "searchAllContacts");
            this.logger.error(`[DirectoryService] searchAllContacts failure -- ${errorInfo.error.message}`);
            throw errorInfo.error;
        }
    }


    // ====================================================
    //                 PRIVATE FUNCTIONS
    // ====================================================


    // ====================================================
    //                     OUTLOOK STUFF
    // ====================================================

    private adaptOutlookContacts(outlookResults: any[]): any[] {
        if (outlookResults?.length) {
            outlookResults.forEach((contact) => {
                if (contact.photo?.src) {
                    contact.avatar = contact.photo;
                }
                else if (contact.photo) {
                    contact.avatar = new Image();
                    contact.avatar.src = contact.photo;
                }
                contact.businessPhones = [];
                if (contact.businessPhone) { contact.businessPhones.push(contact.businessPhone); }
                if (contact.mobilePhone) {
                    contact.mobilePhones = [];
                    contact.mobilePhones.push(contact.mobilePhone);
                }
                if (contact.homePhone) { contact.otherPhoneNumbers = contact.homePhone; }
                if (contact.emailAddress) { contact.email = contact.emailAddress; }

                contact.outlookFlag = true;
            });
        }
        return outlookResults;
    }


    private async searchOutlookUsersByName(searchPattern: any): Promise<any[]> {
        if (this.centralizedService?.isDesktopApp() && this.centralizedService.outlook) {
            try {
                return await this.centralizedService.outlook.searchByNameWithAvatar(searchPattern, 2);
            } catch {
                this.logger.warn("[DirectoryService] searchOutlookUsersByName - Outlook service unavailable");
                return [];
            }
        }
        return [];
    }

    private async searchOutlookUsersByEmail(searchPattern: any): Promise<any[]> {
        if (this.centralizedService?.isDesktopApp() && this.centralizedService.outlook) {
            try {
                return await this.centralizedService.outlook.searchByEmailWithAvatar(searchPattern, 2);
            } catch {
                this.logger.warn("[DirectoryService] searchOutlookUsersByEmail - Outlook service unavailable");
                return [];
            }
        }
        return [];
    }
}
